/**
 * My follow-ups (C-356, Pedro 2026-10-11) — the RM's own Compliance worklist.
 *
 * The Compliance page is Admin and Compliance only. RM, Staff and read-only users see
 * their follow-ups here instead: the same two tables as the Compliance page (same
 * columns, links and filters, §3h), limited to the agreements where they are a Named RM
 * and those agreements' persons, accounts and policies (`scope=mine` on the server).
 *
 * Personal by construction, so the page needs no role gate: whoever opens it sees only
 * their own Named-RM clients (an Admin who is a Named RM sees theirs).
 */
import { Container, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconCalendarTime, IconList } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';

import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { ExpiringDatesSection, IncompleteRecordsSection } from './CompliancePage';

export function MyFollowUpsPage() {
  useDocumentTitle('My follow-ups');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'incomplete' ? 'incomplete' : 'expiring';
  const setTab = (v: string | null) => {
    if (!v) return;
    const next = new URLSearchParams(searchParams);
    if (v === 'expiring') next.delete('tab'); else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>My follow-ups</Title>
        <Text size="sm" c="dimmed">
          Dates and missing information on the agreements where you are a Named RM, and on
          their persons, accounts and policies.
        </Text>
        <Tabs value={tab} onChange={setTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="expiring" leftSection={<IconCalendarTime size={14} />}>
              Expiring dates
            </Tabs.Tab>
            <Tabs.Tab value="incomplete" leftSection={<IconList size={14} />}>
              Incomplete records
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="expiring" pt="md">
            <ExpiringDatesSection scope="mine" />
          </Tabs.Panel>
          <Tabs.Panel value="incomplete" pt="md">
            <IncompleteRecordsSection scope="mine" />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
